import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SocialBar from "@/components/SocialBar";
import {
  Clock, ShieldCheck, ListChecks, CalendarClock, CheckCircle2, ArrowRight,
  QrCode, MapPin, Wallet, Plane, Lock, Sparkles,
} from "lucide-react";

const features = [
  { icon: QrCode, title: "Dynamic QR attendance", desc: "Clock in by scanning a workplace QR that rotates every 30 seconds — no dedicated hardware required." },
  { icon: MapPin, title: "Geofencing & work modes", desc: "On-site radius check-in plus per-employee remote and hybrid schedules." },
  { icon: Clock, title: "Audit-ready logs", desc: "Device-accurate, trusted timestamps with immutable history and full edit trails." },
  { icon: ShieldCheck, title: "Approvals & exceptions", desc: "Early or late clock-outs need a reason and manager sign-off before they close." },
  { icon: Wallet, title: "Payroll & PDF payslips", desc: "Run payroll and auto-email each employee a branded PDF payslip." },
  { icon: Plane, title: "Leave management", desc: "Employees request time off; managers approve within their workplace scope." },
  { icon: ListChecks, title: "Tasks, feedback & AI", desc: "Assign work, collect complaints and suggestions, and get AI-written summaries." },
  { icon: CalendarClock, title: "Meetings & announcements", desc: "Schedule meetings with AI agendas and broadcast company-wide updates." },
  { icon: Lock, title: "Personal Admin", desc: "A private space for each person's renewals, documents and reminders — separate from work data." },
];

const DEFAULT_TIERS = [
  { key: "tier1", label: "1–20 employees", monthly: 30, annual: 300, annual_monthly: 25 },
  { key: "tier2", label: "21–50 employees", monthly: 45, annual: 480, annual_monthly: 40 },
  { key: "tier3", label: "51–100 employees", monthly: 60, annual: 660, annual_monthly: 55 },
  { key: "custom", label: "101+ employees", monthly: null, annual: null, annual_monthly: null },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [currency, setCurrency] = useState("USD");
  const [trialPrice, setTrialPrice] = useState(0.99);
  const trialLabel = `$${Number(trialPrice).toFixed(2)}`;

  useEffect(() => { if (user) navigate("/app"); }, [user, navigate]);
  useEffect(() => {
    api.get("/pricing").then((r) => { if (r.data?.trial_price_usd != null) setTrialPrice(r.data.trial_price_usd); if (r.data?.tiers?.length) { setTiers(r.data.tiers); setCurrency(r.data.currency || "USD"); } }).catch(() => {});
  }, []);

  const cur = (n) => (currency === "NGN" ? "₦" : "$") + Number(n).toLocaleString();

  return (
    <div className="min-h-screen bg-[#0A0D0C] text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-30 glass border-b border-emerald-500/15">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#062217]" />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tight">QwikLogs Africa</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn">Sign in</Button></Link>
            <Link to="/signup"><Button className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="nav-signup-btn">Start {trialLabel} trial</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative grid-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0D0C]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-semibold uppercase tracking-widest">
              <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" /> Workforce, handled
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Employee management that runs on <span className="text-emerald-400 text-glow">precision</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl">
              QwikLogs gives your company QR &amp; geofenced attendance, payroll, leave, tasks, approvals and team comms — in one clean, fast, audit-ready workspace.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold glow-emerald" data-testid="hero-signup-btn">
                  Start 14-day trial for {trialLabel} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login"><Button size="lg" variant="outline" className="border-emerald-500/30">Sign in</Button></Link>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> No card to start</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> QR &amp; geofenced attendance</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Payroll &amp; payslips</span>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: ".15s" }}>
            <div className="absolute -inset-4 bg-emerald-500/20 blur-3xl rounded-full" />
            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Team at work"
              className="relative w-full max-h-[480px] rounded-2xl border border-emerald-500/20 shadow-2xl object-cover"
            />
            <div className="absolute -bottom-5 left-3 sm:left-6 glass border border-emerald-500/25 rounded-xl px-4 py-3 flex items-center gap-3 shadow-xl">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-400" /></div>
              <div><p className="text-sm font-semibold">Clocked in · on-site</p><p className="text-xs text-muted-foreground">Verified 0m from geofence</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-emerald-500/10 bg-[#0B100E]">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[["30s", "QR code rotation"], ["0m", "Geofence precision"], ["14 days", `Trial for ${trialLabel}`], ["1 plan", "Full platform"]].map(([n, l]) => (
            <div key={l} className="animate-fade-up"><p className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-400 text-glow">{n}</p><p className="mt-1 text-xs sm:text-sm text-muted-foreground">{l}</p></div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Everything your team needs, nothing it doesn't</h2>
          <p className="mt-3 text-muted-foreground">One platform for attendance, pay, time off, tasks and communication — built for managers who value clean records and employees who value clarity.</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={f.title} className="card-emerald p-6 animate-fade-up transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/40" style={{ animationDelay: `${i * 0.05}s` }} data-testid="feature-card">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-display text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Pricing that scales with your team</h2>
          <p className="mt-3 text-muted-foreground">One plan per company, priced by active employees. Every tier includes the full platform. Start with a 14-day trial for just {trialLabel} — cancel anytime.</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch" data-testid="pricing-tiers">
          {tiers.map((t) => {
            const custom = t.monthly == null;
            const popular = t.key === "tier2";
            const save = !custom && t.annual != null ? t.monthly * 12 - t.annual : 0;
            return (
              <div key={t.key} data-testid={`pricing-tier-${t.key}`}
                className={`card-emerald p-6 flex flex-col relative ${popular ? "border-emerald-500/40 glow-emerald" : ""}`}>
                {popular && <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-emerald-500 text-[#062217] text-xs font-bold">Most popular</span>}
                <p className="text-xs uppercase tracking-widest text-muted-foreground">{t.label}</p>
                {custom ? (
                  <>
                    <div className="mt-3"><span className="font-display text-4xl font-extrabold">Custom</span></div>
                    <p className="mt-2 text-sm text-muted-foreground flex-1">Volume pricing for large teams. Let's tailor a plan.</p>
                    <Link to="/signup" className="block mt-6"><Button className="w-full" variant="outline" data-testid={`pricing-cta-${t.key}`}>Contact sales</Button></Link>
                  </>
                ) : (
                  <>
                    <div className="mt-3 flex items-end gap-1">
                      <span className={`font-display text-4xl font-extrabold ${popular ? "text-emerald-400" : ""}`}>{cur(t.monthly)}</span>
                      <span className="text-muted-foreground mb-1 text-sm">/month</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">or {cur(t.annual_monthly)}/mo billed annually ({cur(t.annual)}/yr)</p>
                    {save > 0 && <Badge className="mt-2 w-fit bg-emerald-500/15 text-emerald-300">Save {cur(save)}/year</Badge>}
                    <div className="flex-1" />
                    <Link to="/signup" className="block mt-6">
                      <Button className={`w-full ${popular ? "bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" : ""}`} variant={popular ? "default" : "outline"} data-testid={`pricing-cta-${t.key}`}>Start {trialLabel} trial</Button>
                    </Link>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-400" /> Your plan is chosen automatically from your active employee count — no surprises.
        </p>
      </section>

      <footer className="border-t border-emerald-500/15 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-emerald-500 flex items-center justify-center"><Clock className="w-4 h-4 text-[#062217]" /></div>
            <span className="font-display font-bold text-foreground">QwikLogs Africa</span>
          </div>
          <p>© 2026 QwikLogs Africa. Audit-ready workforce management.</p>
          <SocialBar />
        </div>
      </footer>
    </div>
  );
}
